/** Operators PostgREST accepts in a filter query parameter. */
export type FilterOperator =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "like"
  | "ilike"
  | "is"
  | "in";

type Primitive = string | number | boolean;

export type FilterInput =
  | Primitive
  | Primitive[]
  | { op: FilterOperator; value: Primitive | Primitive[] | null };

/**
 * Formats a raw value into a PostgREST filter string, for a generated call's
 * row-filter query parameters (e.g. `GetVehiclesParams.rego`) — those are
 * generated as plain `string`, with no PostgREST-syntax handling of their
 * own, so a value has to be pre-formatted before being passed in.
 *
 * - Primitive: `"Acme"` → `"eq.Acme"`
 * - Array (translated to the `in` operator): `[1, 2, 3]` → `"in.(1,2,3)"`
 * - Object with an explicit operator: `{ op: "gt", value: 30 }` → `"gt.30"`
 *
 * Operator defaults to `eq` when not given explicitly.
 *
 * `null` is only valid with the `is` operator (`{ op: "is", value: null }` →
 * `"is.null"`) — any other `null` usage returns `undefined` since PostgREST
 * has no `eq.null`/`gt.null` etc.
 */
export function postgrestFilter(raw: FilterInput): string | undefined {
  let op: FilterOperator = "eq";
  let value: Primitive | Primitive[] | null;

  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    op = raw.op ?? "eq";
    value = raw.value;
  } else {
    value = raw;
  }

  if (op === "is") return `is.${value}`;

  // `null` is only valid with the `is` operator handled above.
  if (value === null) return undefined;

  if (Array.isArray(value)) return `in.(${value.join(",")})`;

  return `${op}.${value}`;
}
